const ASSET_TYPE = "assetType";
const ASSET_SUBTYPE = "assetSubType";
const ALL_ASSET_TYPES_ID = "allassettypes";

function makeId(prefix, id) {
    return `${prefix}-${id}`;
}

function addToSet(set, id) {
    set[id] = true;
}

function removeFromSet(set, id) {
    if (set[id]) {
        delete set[id];
    }
}

function has(set, id) {
    return !!set[id];
}

function cloneSet(set) {
    let clone = {};
    for (let id in set) {
        if (set.hasOwnProperty(id)) {
            clone[id] = true;
        }
    }
    return clone;
}

function toInt(value) {
    let intValue = parseInt(value, 10);
    return isNaN(intValue) ? null : intValue;
}

function countSelected(children, selectedSubTypes) {
    let count = 0;
    for (let i = 0; i < children.length; i++) {
        if (has(selectedSubTypes, children[i])) {
            count++;
        }
    }
    return count;
}

function isFull(selected, assetTypeId, children) {
    if (!has(selected.selectedAssetTypes, assetTypeId)) {
        return false;
    }
    return !children.some(subTypeId => has(selected.selectedSubTypes, subTypeId));
}

function compressIfAllChildren(selected, assetTypeId, children) {
    if (!children || children.length === 0) {
        return;
    }

    if (children.every(child => has(selected.selectedSubTypes, child))) {
        children.forEach(child => removeFromSet(selected.selectedSubTypes, child));
        addToSet(selected.selectedAssetTypes, assetTypeId);
    }
}

export function parseId(uiId) {
    let dash = uiId.indexOf("-");
    return {
        prefix: uiId.substring(0, dash),
        id: uiId.substring(dash + 1)
    };
}

export function buildAssetTypesIndex(availableAssetTypes) {
    const assetTypeToChildren = {};
    const subTypeToParent = {};

    availableAssetTypes.forEach(assetType => {
        const assetTypeId = assetType.id;
        const children = [];
        (assetType.subtypes || []).forEach(subType => {
            children.push(subType.id);
            subTypeToParent[subType.id] = assetTypeId;
        });
        assetTypeToChildren[assetTypeId] = children;
    });

    return {
        assetTypeChildren: assetTypeId => assetTypeToChildren[assetTypeId] || [],
        parentOfSubType: subTypeId => subTypeToParent[subTypeId] || null
    };
}

export function makeEmptySelection() {
    return { selectedAssetTypes: {}, selectedSubTypes: {} };
}

export function buildGroupCheckBoxModel(availableAssetTypes, selected, idx, expanded) {
    const model = availableAssetTypes.map(assetType => {
        const assetTypeId = String(assetType.id);
        const subTypes = idx.assetTypeChildren(assetTypeId);

        if (subTypes.length === 0) {
            return {
                id: makeId(ASSET_TYPE, assetTypeId),
                name: assetType.name,
                checked: has(selected.selectedAssetTypes, assetTypeId),
                type: "item"
            };
        }

        const full = isFull(selected, assetTypeId, subTypes);
        const partial = !full && countSelected(subTypes, selected.selectedSubTypes) > 0;

        const items = subTypes.map((subTypeId, j) => {
            const subType = assetType.subtypes[j];
            const subTypeChecked = full || has(selected.selectedSubTypes, subTypeId);
            return {
                id: makeId(ASSET_SUBTYPE, subTypeId),
                name: subType.name,
                type: "item",
                checked: subTypeChecked
            };
        });

        return {
            id: makeId(ASSET_TYPE, assetType.id),
            name: assetType.name,
            type: "group",
            expanded: expanded[assetType.id] === true,
            checked: full,
            partialChecked: partial,
            items
        };
    });

    return buildAllModelForCheckBoxList(availableAssetTypes, selected, model, expanded);
}

function buildAllModelForCheckBoxList(availableAssetTypes, selected, children, expanded) {

    const allChecked = allSelected(availableAssetTypes, selected);
    const partialAll = !allChecked && anySelected(selected);

    return [{
        id: ALL_ASSET_TYPES_ID,
        name: "All Asset Types",
        type: "group",
        expanded: expanded[ALL_ASSET_TYPES_ID] === true,
        checked: allChecked,
        partialChecked: partialAll,
        items: children
    }];
}

function anySelected(selected) {
    return Object.keys(selected.selectedAssetTypes).length > 0 || Object.keys(selected.selectedSubTypes).length > 0;
}

function allSelected(available, selected) {
    const totalTypes = available.length;
    const selectedTypes = Object.keys(selected.selectedAssetTypes).length;
    const selectedSubTypes = Object.keys(selected.selectedSubTypes).length;

    return selectedTypes === totalTypes && selectedSubTypes === 0;
}

export function parseSelected(selection) {
    return {
        selectedAssetTypes: Object.keys(selection.selectedAssetTypes)
            .map(toInt)
            .filter(j => j !== null),
        selectedSubTypes: Object.keys(selection.selectedSubTypes)
            .map(toInt)
            .filter(j => j !== null)
    };
}

export function buildSelection(payload) {
    const sel = makeEmptySelection();

    (payload.assetTypes || []).forEach(assetTypeId => {
        addToSet(sel.selectedAssetTypes, String(assetTypeId));
    });

    (payload.subTypes || []).forEach(subTypeId => {
        addToSet(sel.selectedSubTypes, String(subTypeId));
    });

    return sel;
}

export function findChildItem(model, id, type) {
    if (!model) return null;

    for (const element of model) {
        if (element.id === id && element.type === type) {
            return element;
        }

        if (element.items && element.items.length > 0) {
            const found = findChildItem(element.items, id, type);
            if (found) return found;
        }
    }
    return null;
}

export function toggleAssetTypeOrSubType(id, idx, selection) {
    let sel = {
        selectedAssetTypes: cloneSet(selection.selectedAssetTypes),
        selectedSubTypes: cloneSet(selection.selectedSubTypes)
    }

    let parsedId = parseId(id);
    let type = parsedId.prefix, actualId = parsedId.id;
    if (type === ASSET_TYPE) {
        let kids = idx.assetTypeChildren(actualId);
        if (!kids || kids.length === 0) {
            if (has(sel.selectedAssetTypes, actualId)) {
                removeFromSet(sel.selectedAssetTypes, actualId);
            } else {
                addToSet(sel.selectedAssetTypes, actualId);
            }
            return sel;
        }
    }

    let parentId = idx.parentOfSubType(actualId);
    if (!parentId) {
        return sel;
    }

    let children = idx.assetTypeChildren(parentId);

    if (isFull(sel, parentId, children)) {
        for (let i = 0; i < children.length; i++) {
            let child = children[i];
            if (actualId === child) {
                removeFromSet(sel.selectedSubTypes, actualId);
            } else {
                addToSet(sel.selectedSubTypes, child);
            }
        }
        return sel;
    }

    if (has(sel.selectedSubTypes, actualId)) {
        removeFromSet(sel.selectedSubTypes, actualId);
        if (Object.keys(sel.selectedSubTypes).length === 0) {
            removeFromSet(sel.selectedAssetTypes, parentId);
        }
    } else {
        addToSet(sel.selectedSubTypes, actualId);
        if (!has(sel.selectedAssetTypes, parentId)) {
            addToSet(sel.selectedAssetTypes, parentId);
        }
        compressIfAllChildren(sel, parentId, children);
    }

    return sel;
}

export function toggleAssetTypeWithSubTypes(id, idx, selection) {
    const sel = {
        selectedAssetTypes: cloneSet(selection.selectedAssetTypes),
        selectedSubTypes: cloneSet(selection.selectedSubTypes)
    };

    const { id: actualId } = parseId(id);
    const children = idx.assetTypeChildren(actualId);

    if (isFull(sel, actualId, children)) {
        removeFromSet(sel.selectedAssetTypes, actualId);
        return sel;
    }

    addToSet(sel.selectedAssetTypes, actualId);
    children.forEach(child => removeFromSet(sel.selectedSubTypes, child));

    return sel;
}

export function toggleAllAssetTypes(selected, available) {
    const sel = {
        selectedAssetTypes: cloneSet(selected.selectedAssetTypes),
        selectedSubTypes: cloneSet(selected.selectedSubTypes)
    };

    if (anySelected(selected)) {
        sel.selectedAssetTypes = {};
        sel.selectedSubTypes = {};
    } else {
        sel.selectedSubTypes = {};
        available.forEach(assetType => {
            addToSet(sel.selectedAssetTypes, String(assetType.id));
        });
    }

    return sel;
}

export function isAllId(id) {
    return id === ALL_ASSET_TYPES_ID;
}